import { openDB, type IDBPDatabase } from 'idb';
import * as Y from 'yjs';

const DB_NAME = 'rbv-vault';
const COMPACT_AFTER = 64;

/** Origin used when applying locally persisted state so it isn't re-persisted
 * or treated as a remote/server change. */
export const IDB_ORIGIN = 'idb';

/**
 * Single-database IndexedDB persistence for every Y.Doc in the vault
 * (y-indexeddb creates one IDB database per doc, which does not scale to a
 * ~1k-doc vault on iOS Safari — so we keep one DB with a docKey index).
 */
export class VaultPersistence {
  private db: IDBPDatabase | null = null;
  private counts = new Map<string, number>();

  async open(): Promise<void> {
    this.db = await openDB(DB_NAME, 1, {
      upgrade(db) {
        const updates = db.createObjectStore('updates', { autoIncrement: true });
        updates.createIndex('byDoc', 'docKey');
        db.createObjectStore('snapshots');
      },
    });
  }

  /**
   * Persist all future updates of the doc and load its stored state.
   *
   * The update listener is attached synchronously BEFORE any stored state is
   * read, so content inserted while the async load is still in flight (e.g.
   * a template written right after createNote) is never lost — stored state
   * itself is applied with IDB_ORIGIN, which the listener ignores.
   */
  async bind(docKey: string, doc: Y.Doc): Promise<void> {
    const db = this.db;
    if (!db) throw new Error('persistence not opened');

    doc.on('update', (update: Uint8Array, origin: unknown) => {
      if (origin === IDB_ORIGIN) return;
      this.persist(docKey, doc, update).catch((err) => {
        console.error(`[persistence] write failed for ${docKey} — local durability degraded`, err);
      });
    });

    const snap = (await db.get('snapshots', docKey)) as Uint8Array | undefined;
    if (snap) Y.applyUpdate(doc, snap, IDB_ORIGIN);
    const tx = db.transaction('updates', 'readonly');
    const idx = tx.store.index('byDoc');
    let count = 0;
    for (let cur = await idx.openCursor(IDBKeyRange.only(docKey)); cur; cur = await cur.continue()) {
      Y.applyUpdate(doc, (cur.value as { data: Uint8Array }).data, IDB_ORIGIN);
      count++;
    }
    await tx.done;
    this.counts.set(docKey, (this.counts.get(docKey) ?? 0) + count);
  }

  private async persist(docKey: string, doc: Y.Doc, update: Uint8Array) {
    const db = this.db;
    if (!db) return;
    await db.put('updates', { docKey, data: update });
    const n = (this.counts.get(docKey) ?? 0) + 1;
    this.counts.set(docKey, n);
    if (n >= COMPACT_AFTER) {
      this.counts.set(docKey, 0);
      await this.compact(docKey, doc);
    }
  }

  /**
   * Compact the update log into the snapshot. Multi-tab safe: everything
   * happens in ONE readwrite transaction — rows written by another tab are
   * read, merged into the new snapshot, and only the rows actually read are
   * deleted, so a second tab's offline edits are never destroyed (the
   * y-indexeddb approach). An optional Web Lock serializes compactions.
   */
  private async compact(docKey: string, doc: Y.Doc) {
    const run = async () => {
      const db = this.db;
      if (!db) return;
      const tx = db.transaction(['snapshots', 'updates'], 'readwrite');
      const snapStore = tx.objectStore('snapshots');
      const updStore = tx.objectStore('updates');
      const prevSnap = (await snapStore.get(docKey)) as Uint8Array | undefined;
      const pieces: Uint8Array[] = [];
      if (prevSnap) pieces.push(prevSnap);
      const keys: IDBValidKey[] = [];
      const idx = updStore.index('byDoc');
      for (let cur = await idx.openCursor(IDBKeyRange.only(docKey)); cur; cur = await cur.continue()) {
        pieces.push((cur.value as { data: Uint8Array }).data);
        keys.push(cur.primaryKey);
      }
      // Include this tab's live state (it may hold remote updates that were
      // never individually persisted here).
      pieces.push(Y.encodeStateAsUpdate(doc));
      await snapStore.put(Y.mergeUpdates(pieces), docKey);
      for (const k of keys) await updStore.delete(k);
      await tx.done;
    };
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      await navigator.locks.request(`rbv-compact:${docKey}`, run);
    } else {
      await run();
    }
  }

  /** Drop all local data (logout / debugging). */
  async wipe(): Promise<void> {
    this.db?.close();
    this.db = null;
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    });
  }
}
